import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fromZonedTime } from 'date-fns-tz';
import { supabase } from '@/integrations/supabase/client';
import type { EventOccurrence, EventOccurrenceWithBookings } from '@/types/events';

const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

/** Returns true if d is a valid Date */
function isValidDate(d: Date): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}

/** Parse a date string + HH:mm time into a TZ-aware ISO string. Throws on invalid input. */
function parseZonedISO(dateStr: string, time: string, tz: string): string {
  const d = fromZonedTime(`${dateStr} ${time}:00`, tz);
  if (!isValidDate(d)) {
    throw new Error('Välj ett giltigt datum och en starttid.');
  }
  return d.toISOString();
}

const TZ = 'Europe/Stockholm';

// Fetch occurrences for an event (producer view)
export function useEventOccurrences(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-occurrences', eventId],
    queryFn: async (): Promise<EventOccurrenceWithBookings[]> => {
      if (!eventId) return [];
      
      const { data, error } = await supabase
        .rpc('get_event_occurrences_with_bookings' as any, {
          p_event_id: eventId,
          p_public: false,
        });
      
      if (error) throw error;
      return (data || []) as EventOccurrenceWithBookings[];
    },
    enabled: !!eventId,
  });
}

// Fetch public occurrences for an event (visitor view - only scheduled, future)
export function usePublicEventOccurrences(eventId: string | undefined) {
  return useQuery({
    queryKey: ['public-event-occurrences', eventId],
    queryFn: async (): Promise<EventOccurrenceWithBookings[]> => {
      if (!eventId) return [];
      
      const { data, error } = await supabase
        .rpc('get_event_occurrences_with_bookings' as any, {
          p_event_id: eventId,
          p_public: true,
        });
      
      if (error) throw error;
      return ((data || []) as EventOccurrenceWithBookings[])
        .filter((row) => row.status === 'scheduled' && row.start_at >= new Date().toISOString());
    },
    enabled: !!eventId,
  });
}

// Create occurrence
export function useCreateOccurrence() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Omit<EventOccurrence, 'id' | 'created_at' | 'updated_at'>) => {
      const { data: occurrence, error } = await supabase
        .from('event_occurrences')
        .insert(data)
        .select()
        .single();
      
      if (error) throw error;
      return occurrence as EventOccurrence;
    },
    onSuccess: (occurrence) => {
      queryClient.invalidateQueries({ queryKey: ['event-occurrences', occurrence.event_id] });
    },
  });
}

// Update occurrence
export function useUpdateOccurrence() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<EventOccurrence> & { id: string }) => {
      const { data: occurrence, error } = await supabase
        .from('event_occurrences')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return occurrence as EventOccurrence;
    },
    onSuccess: (occurrence) => {
      queryClient.invalidateQueries({ queryKey: ['event-occurrences', occurrence.event_id] });
    },
  });
}

// Delete occurrence (only if no bookings)
export function useDeleteOccurrence() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, eventId }: { id: string; eventId: string }) => {
      const { error } = await supabase
        .from('event_occurrences')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { id, eventId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-occurrences', variables.eventId] });
    },
  });
}

// Sync occurrences - create/delete based on selected dates
export function useSyncOccurrences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      eventId,
      selectedDates,
      startTime,
      endTime,
    }: {
      eventId: string;
      selectedDates: string[]; // YYYY-MM-DD format
      startTime: string; // HH:mm format
      endTime: string; // HH:mm format
    }) => {
      // 1. Fetch existing occurrences
      const { data: existingOccurrences, error: fetchError } = await supabase
        .rpc('get_event_occurrences_with_bookings' as any, {
          p_event_id: eventId,
          p_public: false,
        });
      
      if (fetchError) throw fetchError;
      
      const existing = (existingOccurrences || []) as EventOccurrenceWithBookings[];
      
      // 2. Build a map of existing occurrences by date
      const existingByDate = new Map<string, EventOccurrenceWithBookings>();
      existing.forEach(occ => {
        const dateStr = occ.start_at.split('T')[0];
        existingByDate.set(dateStr, occ);
      });
      
      // 3. Determine which to create, keep, or remove
      const toCreate: string[] = [];
      const toKeep = new Set<string>();
      const toCancel: EventOccurrenceWithBookings[] = [];
      const toDelete: EventOccurrenceWithBookings[] = [];
      
      // Find dates to create
      selectedDates.forEach(dateStr => {
        if (!existingByDate.has(dateStr)) {
          toCreate.push(dateStr);
        } else {
          toKeep.add(dateStr);
        }
      });
      
      // Find occurrences to remove
      existing.forEach(occ => {
        const dateStr = occ.start_at.split('T')[0];
        if (!selectedDates.includes(dateStr) && occ.status !== 'cancelled') {
          if (occ.booked_spots > 0) {
            // Has bookings - cancel instead of delete
            toCancel.push(occ);
          } else {
            // No bookings - safe to delete
            toDelete.push(occ);
          }
        }
      });
      
      // 4. Execute changes
      const results = {
        created: 0,
        cancelled: 0,
        deleted: 0,
        cantDelete: [] as string[],
      };
      
      // Validate start time
      if (!startTime || !/^\d{2}:\d{2}$/.test(startTime)) {
        throw new Error('Välj ett giltigt datum och en starttid.');
      }

      // Create new occurrences
      if (toCreate.length > 0) {
        const newOccurrences = toCreate.map(dateStr => {
          const startIso = parseZonedISO(dateStr, startTime, TZ);

          let endIso: string;
          if (endTime && /^\d{2}:\d{2}$/.test(endTime)) {
            endIso = parseZonedISO(dateStr, endTime, TZ);
            // If end <= start, default to start + 2h
            if (endIso <= startIso) {
              endIso = new Date(new Date(startIso).getTime() + DEFAULT_DURATION_MS).toISOString();
            }
          } else {
            // No end time provided — default +2h
            endIso = new Date(new Date(startIso).getTime() + DEFAULT_DURATION_MS).toISOString();
          }

          return {
            event_id: eventId,
            start_at: startIso,
            end_at: endIso,
            status: 'scheduled' as const,
            capacity: null,
          };
        });
        
        const { error: createError } = await supabase
          .from('event_occurrences')
          .insert(newOccurrences);
        
        if (createError) throw createError;
        results.created = toCreate.length;
      }
      
      // Cancel occurrences with bookings
      for (const occ of toCancel) {
        const { error: updateError } = await supabase
          .from('event_occurrences')
          .update({ status: 'cancelled' })
          .eq('id', occ.id);
        
        if (updateError) throw updateError;
        results.cancelled++;
        
        const dateStr = occ.start_at.split('T')[0];
        results.cantDelete.push(dateStr);
      }
      
      // Delete occurrences without bookings
      if (toDelete.length > 0) {
        const idsToDelete = toDelete.map(o => o.id);
        const { error: deleteError } = await supabase
          .from('event_occurrences')
          .delete()
          .in('id', idsToDelete);
        
        if (deleteError) throw deleteError;
        results.deleted = toDelete.length;
      }
      
      return results;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-occurrences', variables.eventId] });
    },
  });
}

// Cancel a single occurrence
export function useCancelOccurrence() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, eventId }: { id: string; eventId: string }) => {
      const { data, error } = await supabase
        .from('event_occurrences')
        .update({ status: 'cancelled' })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { occurrence: data, eventId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['event-occurrences', result.eventId] });
    },
  });
}
